import React from 'react';
import {
  Anchor,
  Button,
  Grid,
  Pagination,
  Skeleton,
  TextInput
} from '@mantine/core';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import CreationWizardButton from './CreationWizardButton';
import AppText from '../../common/AppText';

const MAX_FEATURED_STOCK_CONTENT = 4;
const MAX_STOCK_CONTENT_PER_PAGE = 6;

const StoreCreationStockContentSelection = ({
  loading,
  createOptions,
  formState,
  setFormState,
  viewState,
  setViewState,
  isSingleSelect,
  showThemeDetails
}) => {
  const filteredStockContent =
    createOptions?.ecomStockContent
      .filter(
        (s) =>
          !viewState.search ||
          s.title.toLowerCase().includes(viewState.search.toLowerCase())
      )
      .sort((a, b) => a.title.localeCompare(b.title)) || [];

  return (
    <>
      {viewState.showAllStockContent && (
        <TextInput
          onChange={(e) =>
            setViewState({
              ...viewState,
              search: e.currentTarget.value,
              pageIndex: 1
            })
          }
          placeholder="Search stock content"
          value={viewState.search}
        />
      )}

      {viewState.showAllStockContent && filteredStockContent.length === 0 ? (
        <AppStack
          style={{
            padding: 40,
            paddingTop: 20,
            gap: 12,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <AppText
            style={{
              textAlign: 'center',
              color: '#666',
              fontStyle: 'italic',
              fontSize: 14
            }}
          >
            No stock content found by search
          </AppText>
          <Button
            color="dark"
            onClick={() => {
              setViewState({
                ...viewState,
                search: '',
                pageIndex: 1
              });
            }}
            variant="outline"
          >
            Clear search
          </Button>
        </AppStack>
      ) : (
        <Grid>
          {loading || !createOptions
            ? Array(4)
                .fill()
                .map((_, s) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid.Col key={s} span={6}>
                    <AppCard
                      radius={8}
                      shadow="sm"
                      style={{ padding: 0 }}
                      withBorder
                    >
                      <AppFlexbox style={{ minHeight: 60, padding: 12 }}>
                        <AppStack style={{ flex: 1, gap: 3 }}>
                          <Skeleton height={10} mb={6} width="50%" />
                          <Skeleton height={10} width="40%" />
                        </AppStack>
                      </AppFlexbox>
                    </AppCard>
                  </Grid.Col>
                ))
            : (viewState.showAllStockContent
                ? filteredStockContent.slice(
                    (viewState.pageIndex - 1) * MAX_STOCK_CONTENT_PER_PAGE,
                    viewState.pageIndex * MAX_STOCK_CONTENT_PER_PAGE
                  )
                : createOptions.ecomStockContent
                    .slice(0, MAX_FEATURED_STOCK_CONTENT)
                    .sort((a, b) => a.title.localeCompare(b.title))
              ).map((s) => {
                const selected = !!formState.ecomStockContent.find(
                  (e) => e.pkEcomStockContent === s.pkEcomStockContent
                );
                return (
                  <Grid.Col key={s.pkEcomStockContent} span={6}>
                    <CreationWizardButton
                      description={s.description}
                      label={s.title}
                      onClick={() => {
                        let updatedStockContent = [
                          ...formState.ecomStockContent
                        ];
                        if (selected) {
                          updatedStockContent = updatedStockContent.filter(
                            (e) => e.pkEcomStockContent !== s.pkEcomStockContent
                          );
                        }
                        else if (isSingleSelect) {
                          updatedStockContent = [
                            {
                              pkEcomStockContent: s.pkEcomStockContent,
                              pkEcomVendorProducts: []
                            }
                          ];
                        }
                        else {
                          updatedStockContent.push({
                            pkEcomStockContent: s.pkEcomStockContent,
                            pkEcomVendorProducts: []
                          });
                        }
                        setFormState({
                          ...formState,
                          copyThemeFromStockContent: true,
                          ecomStockContent: updatedStockContent
                        });
                      }}
                      selected={selected}
                    >
                      {showThemeDetails && (
                        <AppStack style={{ gap: 0 }}>
                          <Anchor
                            component={Link}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ color: 'dodgerblue', fontSize: 14 }}
                            target="_blank"
                            to={`/store/${s.ecomStore.urlHandle}`}
                          >
                            View theme
                          </Anchor>
                        </AppStack>
                      )}
                    </CreationWizardButton>
                  </Grid.Col>
                );
              })}
        </Grid>
      )}

      {viewState.showAllStockContent &&
        filteredStockContent.length > MAX_STOCK_CONTENT_PER_PAGE && (
          <AppFlexbox style={{ justifyContent: 'center' }}>
            <Pagination
              color="blue"
              onChange={(pageIndex) => {
                setViewState({ ...viewState, pageIndex });
              }}
              total={Math.ceil(
                filteredStockContent.length / MAX_STOCK_CONTENT_PER_PAGE
              )}
              value={viewState.pageIndex}
            />
          </AppFlexbox>
        )}

      {createOptions?.ecomStockContent.length > 4 &&
        !viewState.showAllStockContent && (
          <AppFlexbox style={{ justifyContent: 'center', marginTop: 10 }}>
            <Button
              color="blue"
              onClick={() => {
                setViewState({ ...viewState, showAllStockContent: true });
              }}
              rightSection={<ChevronDown size={18} />}
              size="compact-sm"
              style={{ fontSize: 14 }}
              variant="subtle"
            >
              Show all stock content
            </Button>
          </AppFlexbox>
        )}

      {createOptions?.ecomStockContent.length > 4 &&
        viewState.showAllStockContent &&
        filteredStockContent.length > 0 && (
          <AppFlexbox style={{ justifyContent: 'center', marginTop: 10 }}>
            <Button
              color="blue"
              onClick={() => {
                setViewState({
                  ...viewState,
                  showAllStockContent: false,
                  pageIndex: 1,
                  search: ''
                });
              }}
              rightSection={<ChevronUp size={18} />}
              size="compact-sm"
              style={{ fontSize: 14 }}
              variant="subtle"
            >
              Only show featured content
            </Button>
          </AppFlexbox>
        )}
    </>
  );
};

StoreCreationStockContentSelection.propTypes = {
  createOptions: PropTypes.object,
  formState: PropTypes.object,
  isSingleSelect: PropTypes.bool,
  isSportsHeadzAdmin: PropTypes.bool,
  loading: PropTypes.bool,
  setFormState: PropTypes.func,
  setViewState: PropTypes.func,
  showAllStockContent: PropTypes.bool,
  showThemeDetails: PropTypes.bool,
  viewState: PropTypes.object
};

export default StoreCreationStockContentSelection;
